import { useCallback, useEffect, useReducer } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAlert } from '../Components/AlertContext';
import Breadcrumbs from '../Components/Breadcrumbs';
import Button from '../Components/Buttons';
import { ConfirmationProvider } from '../Components/ConfirmationContext';
import { CustomInput, CustomNumberInput, CustomSelect } from '../Components/CustomFormElements';
import EntityBoxWrapper from '../Components/EntityBox';
import PageLoader from '../Components/PageLoader';
import PromptAnalyzerModal from '../Components/PromptAnalyzerModal';
import {
  CoDPattern,
  RealtimePrompt,
  RisenPattern,
  RodesPattern,
  SimplePrompt,
  SwarmPattern,
} from '../Components/PromptPatternForm';
import { PromptApiProvider, usePromptApi } from '../Services/PromptApiProvider';
import { PatternType, PromptCreateApi } from '../Services/PromptApiProviderTypes';
import './Prompt.css';
import {
  buildDefinition,
  getOptimizePromptText,
  mapResponseToFormData,
  validateForm,
} from './PromptHelpers';
import { formReducer, initialFormState, initialPageState, pageReducer } from './PromptReducer';

const PATTERN_OPTIONS = [
  { value: 'simple_prompt', label: 'Simple prompt' },
  { value: 'rodes', label: 'RODES' },
  { value: 'risen', label: 'RISEN' },
  { value: 'chain_of_density', label: 'Chain of Density' },
  { value: 'swarm', label: 'Swarm' },
  { value: 'realtime', label: 'Realtime' },
];

function PromptContent({ canEdit }: { canEdit: boolean }) {
  const { organizationId, promptId } = useParams<{ organizationId: string; promptId: string }>();
  const navigate = useNavigate();
  const { showAlert } = useAlert();
  const { getPromptById, createPrompt, updatePrompt } = usePromptApi();

  const [form, formDispatch] = useReducer(formReducer, initialFormState);
  const [page, pageDispatch] = useReducer(pageReducer, initialPageState);

  const setField = useCallback((field: keyof typeof form, value: string) => {
    formDispatch({ type: 'SET_FIELD', field, value });
  }, []);

  // --- Load prompt ---

  useEffect(() => {
    const loadPrompt = async () => {
      pageDispatch({ type: 'SET_LOADING', value: true });
      try {
        if (promptId !== 'new-prompt') {
          const response = await getPromptById(promptId!);
          formDispatch({ type: 'LOAD_PROMPT', data: mapResponseToFormData(response) });
        }
      } catch (err) {
        pageDispatch({ type: 'SET_ERROR', value: String(err) });
      } finally {
        pageDispatch({ type: 'SET_LOADING', value: false });
      }
    };
    loadPrompt();
  }, [promptId]);

  useEffect(() => {
    pageDispatch({ type: 'SET_IS_NEW', value: promptId === 'new-prompt' });
    pageDispatch({ type: 'SET_IS_DISABLED', value: !canEdit });
  }, [promptId, canEdit]);

  // --- Save ---

  const onSave = useCallback(async () => {
    if (!validateForm(form, showAlert)) return;

    const data: PromptCreateApi = {
      name: form.name,
      pattern_type: form.patternType!,
      definition: buildDefinition(form),
      sort_order: form.sortOrder,
    };

    pageDispatch({ type: 'SET_LOADING', value: true });

    try {
      if (page.isNew) {
        await createPrompt(data);
        showAlert('success', 'Prompt created successfully!');
      } else {
        await updatePrompt(promptId!, data);
        showAlert('success', 'Prompt updated successfully!');
      }
      navigate(`/organizations/${organizationId}/prompts-hub`);
    } catch (err) {
      console.error('Error saving prompt:', err);
      pageDispatch({ type: 'SET_ERROR', value: String(err) });
      showAlert('fail', 'Failed to save the prompt.');
    } finally {
      pageDispatch({ type: 'SET_LOADING', value: false });
    }
  }, [form, page.isNew, promptId, organizationId]);

  const onCancel = useCallback(() => {
    navigate(`/organizations/${organizationId}/prompts-hub`);
  }, [organizationId, navigate]);

  // --- Pattern form ---

  const renderEditForm = () => {
    const { patternType } = form;
    const disabled = page.isDisabled;

    switch (patternType) {
      case 'simple_prompt':
        return (
          <SimplePrompt
            promptValue={form.prompt}
            setPromptValue={(v: string) => setField('prompt', v)}
            isDisabled={disabled}
          />
        );
      case 'rodes':
        return (
          <RodesPattern
            roleValue={form.role}
            setRoleValue={(v: string) => setField('role', v)}
            objectiveValue={form.objective}
            setObjectiveValue={(v: string) => setField('objective', v)}
            detailsValue={form.details}
            setDetailsValue={(v: string) => setField('details', v)}
            examplesValue={form.examples}
            setExamplesValue={(v: string) => setField('examples', v)}
            checkValue={form.checks}
            setCheckValue={(v: string) => setField('checks', v)}
            isDisabled={disabled}
          />
        );
      case 'risen':
        return (
          <RisenPattern
            roleValue={form.role}
            setRoleValue={(v: string) => setField('role', v)}
            instructionValue={form.instruction}
            setInstructionValue={(v: string) => setField('instruction', v)}
            goalValue={form.goal}
            setGoalValue={(v: string) => setField('goal', v)}
            stepsValue={form.stepsToComplete}
            setStepsValue={(v: string) => setField('stepsToComplete', v)}
            narrowingValue={form.narrowing}
            setNarrowingValue={(v: string) => setField('narrowing', v)}
            isDisabled={disabled}
          />
        );
      case 'chain_of_density':
        return (
          <CoDPattern
            instructionValue={form.instruction}
            setInstructionValue={(v: string) => setField('instruction', v)}
            recursionValue={form.recursion}
            setRecursionValue={(v: string) => setField('recursion', v)}
            benchmarkValue={form.benchmark}
            setBenchmarkValue={(v: string) => setField('benchmark', v)}
            guidelinesValue={form.additionalGuidelines}
            setGuidelinesValue={(v: string) => setField('additionalGuidelines', v)}
            isDisabled={disabled}
          />
        );
      case 'swarm':
        return (
          <SwarmPattern
            instructionValue={form.instruction}
            setInstructionValue={(v: string) => setField('instruction', v)}
            roleValue={form.role}
            setRoleValue={(v: string) => setField('role', v)}
            isDisabled={disabled}
          />
        );
      case 'realtime':
        return (
          <RealtimePrompt
            speakerValue={form.speaker}
            setSpeakerValue={(v: string) => setField('speaker', v)}
            listenerValue={form.listener}
            setListenerValue={(v: string) => setField('listener', v)}
            listenerRoleValue={form.listenerRole}
            setListenerRoleValue={(v: string) => setField('listenerRole', v)}
            isDisabled={disabled}
          />
        );
      default:
        return null;
    }
  };

  // --- Render ---

  if (page.loading) return <PageLoader />;
  return (
    <div className="container">
      <Breadcrumbs />
      <div className="row d-flex gap">
        <div className="radius-yes p-2 bg-yes col-6">
          <h2 className="h2-title">{form.name}</h2>
          <div className="gap d-flex flex-row-to-col flex-column">
            <div className="int-col-3 p-2 border-yes radius-yes full-width-on-sml-scr">
              <h4 className="h4-title">Main Settings</h4>

              <div className="input-block d-flex flex-row justify-between">
                <div className="input-description">
                  <div className="input-title">Name</div>
                  <div className="small-font">Prompt template name</div>
                </div>
                <div className="align-wrapper d-flex align-center">
                  <CustomInput
                    id="prompt-name"
                    label="Prompt Name"
                    placeholder="Prompt name"
                    icon={<i className="fa-solid fa-right-to-bracket"></i>}
                    type="text"
                    value={form.name}
                    onChange={(_id: string, value: string) => setField('name', value)}
                    disabled={page.isDisabled}
                  />
                </div>
              </div>

              <div className="input-block d-flex flex-row justify-between">
                <div className="input-description">
                  <div className="input-title">Prompt Pattern</div>
                  <div className="small-font">Select prompt pattern</div>
                </div>
                <div className="align-wrapper d-flex align-center">
                  <CustomSelect
                    id="prompt-pattern"
                    label="Pattern"
                    options={PATTERN_OPTIONS}
                    value={form.patternType}
                    onChange={(val: string) =>
                      formDispatch({ type: 'SET_PATTERN_TYPE', value: val as PatternType })
                    }
                    multiSelect={false}
                    disabled={page.isDisabled}
                  />
                </div>
              </div>

              <div className="input-block d-flex flex-row justify-between">
                <div className="input-description">
                  <div className="input-title">Order</div>
                  <div className="small-font">
                    Prompts with a lower order are sent to the agent first
                  </div>
                </div>
                <div className="align-wrapper d-flex align-center">
                  <CustomNumberInput
                    id="prompt-sort-order"
                    label="Order"
                    value={form.sortOrder}
                    min={0}
                    onChange={(value: number) => formDispatch({ type: 'SET_SORT_ORDER', value })}
                    disabled={page.isDisabled}
                  />
                </div>
              </div>

              {renderEditForm()}
            </div>

            <div className="buttons-container d-flex justify-end">
              <Button
                className="optimize-trigger-btn"
                disabled={page.isDisabled}
                onClick={() => pageDispatch({ type: 'SET_OPTIMIZER_OPEN', value: true })}
              >
                ✦ Analyze
              </Button>
              <Button variant="primary" size="medium" disabled={page.isDisabled} onClick={onSave}>
                Save
              </Button>
              <Button variant="danger" size="medium" onClick={onCancel}>
                Cancel
              </Button>
            </div>
          </div>
        </div>

        <PromptAnalyzerModal
          promptText={getOptimizePromptText(form)}
          patternType={form.patternType ?? undefined}
          isOpen={page.isOptimizerOpen}
          onClose={() => pageDispatch({ type: 'SET_OPTIMIZER_OPEN', value: false })}
          onApply={(newText: string) => {
            if (form.patternType === 'simple_prompt') {
              setField('prompt', newText);
            }
          }}
        />

        <div className="radius-yes p-2 bg-yes col-4">
          <EntityBoxWrapper />
        </div>
      </div>
    </div>
  );
}

function Prompt({ canEdit }: { canEdit: boolean }) {
  return (
    <ConfirmationProvider>
      <PromptApiProvider>
        <PromptContent canEdit={canEdit} />
      </PromptApiProvider>
    </ConfirmationProvider>
  );
}

export default Prompt;
